import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button } from "react-bootstrap";
import { useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";

import { DocumentToolbarConstants, FolderToolbarConstants, MessageToolbarConstants } from "src/Core/Common/Constants";
import { ArchiveFolderType, DetailedStatus, FolderEventType, FolderType } from "src/Core/Common/Enums";
import { IFolderModel } from "src/Core/ViewModels/Folder/FolderModel";
import TreeList from "../ui/TreeList/TreeList";
import { LoadingOverlay, Loader } from "react-overlay-loader";
import { ApplicationState } from "src/store";
import "../ui/Tree/style.scss";
import { fetchFoldersTree, fetchRetentionPeriods, moveFolders } from "src/actionCreators/folderActionCreators";
import { getDaysFromRetentionPeriodId, getResourceLocators, getRetentionOptions } from "src/helper/HelperFunctions";
import { ResourceIdLocators } from "src/helper/ResourceIdLocators";
import { Option } from "react-dropdown";
import { CreateFolderModal } from "../Modal/CreateFolderModal";
import { IDocumentRequestListModel } from "src/Core/ViewModels/documentRequest/DocumentRequestModel";
import { ColorConstants } from "src/assets/custom/colors";
import { InfoCircleIcon } from "src/assets/images/svg/SideMenuIconCollection";
import { isMoveToFolderPopupOpen } from "src/actionCreators/signalRActionCreators";
import { fetchRetentionSettings } from "src/actionCreators/adminSettingsActionCreators";
import { fetchUserSettings } from "src/actionCreators/userSettingsActionCreators";

export const MoveFolderModal: React.FC = () => {
    const params = useParams();
    const dispatch = useDispatch();
    const [isDocumentRequest] = useState<boolean>(params.isDocumentRequest === "true");

    const [moveToFolder, setMoveToFolder] = useState<IFolderModel | undefined>();
    const [showMoveToFolderWarning, toggleMoveToFolderWarning] = useState(false);
    const [allowFormSubmit, toggleAllowFormSubmit] = useState(true);
    const [warningMessageText, setWarningMessageText] = useState("");
    const [showAddFolderModal, setShowAddFolderModal] = useState(false);
    const [retentionPeriods, setRetentionPeriods] = useState<Option[]>([]);
    const selectedMessages = useSelector((appState: ApplicationState) =>
        appState.movingMessages.filter((message: any) => message.folderId !== FolderType.SENT)
    );
    const selectedRequests = useSelector((appState: ApplicationState) => appState.movingRequests);
    const selectedFolders = useSelector((appState: ApplicationState) => appState.folderState.movingFolders);
    const foldersTreeData = useSelector((appState: ApplicationState) => appState.folderState.foldersTreeData);
    const retentionData = useSelector((appState: ApplicationState) => appState.folderState.retentionPeriodsData);
    const isMovingFolder = useSelector((appState: ApplicationState) => appState.folderState.isMovingFolder);
    const resourceLocators = useMemo(() => {
        return getResourceLocators(+params.folderType);
    }, [ResourceIdLocators]);
    const { messageOptions: messageOptionsSettings } = useSelector(
        (appState: ApplicationState) => appState.adminSettingsState
    );
    const { userSettings } = useSelector((appState: ApplicationState) => appState.userSettingsState);

    useEffect(() => {
        dispatch(fetchRetentionPeriods());
        dispatch(fetchRetentionSettings());
        dispatch(fetchUserSettings());
    }, []);

    useEffect(() => {
        if (moveToFolder) {
            let folderRetention = retentionData.retentionPeriods;
            let maxRetention = 0;
            if (messageOptionsSettings.retentionSettings?.isUserAllowedToChangeFolderRetentionPeriod) {
                if (moveToFolder?.folderId === 0) {
                    maxRetention = getDaysFromRetentionPeriodId(userSettings?.retentionSettings.defaultRetentionPeriods.folders
                        || messageOptionsSettings.retentionSettings.defaultRetentionPeriods.folders, retentionData.retentionPeriods);
                } else {
                    maxRetention = moveToFolder?.expiresDays ?? 0;
                }
                folderRetention = retentionData.retentionPeriods.filter(rp => rp.days <= maxRetention);
            }
            setRetentionPeriods(getRetentionOptions(folderRetention));
        }
    }, [retentionData.retentionPeriods.length, moveToFolder?.folderId, userSettings?.retentionSettings.defaultRetentionPeriods, messageOptionsSettings.retentionPeriodList, messageOptionsSettings.retentionSettings]);

    useEffect(() => {
        getFoldersData();
    }, []);

    const getFoldersData = () => {
        let folderIds: number[] = [];
        switch (+params.folderType) {
            case FolderType.SENT:
                folderIds = [FolderType.SENT, FolderType.INBOX, FolderType.DOCUMENTREQUEST];
                break;
            case FolderType.ARCHIVE:
                if (isDocumentRequest) {
                    folderIds = [FolderType.INBOX];
                }
                else {
                    folderIds = [FolderType.DOCUMENTREQUEST];
                }

                if (isMovingFolder) {
                    folderIds = [FolderType.DOCUMENTREQUEST, FolderType.INBOX];
                }
                break;
            case FolderType.INBOX:
            case FolderType.DOCUMENTREQUEST:
                folderIds = [FolderType.INBOX, FolderType.DOCUMENTREQUEST];
                break;
            default:
                break;
        }

        dispatch(fetchFoldersTree([], folderIds));
    };

    const checkFolderExpiry = (destinationFolder: IFolderModel): boolean => {
        if (isMovingFolder) {
            // Check retention period of the folders
            if (!selectedFolders.every(f => f.expiresDays <= destinationFolder.expiresDays)) {
                setWarningMessageText(FolderToolbarConstants.DESTINATION_FOLDER_EXPIRY_WARNING);
                toggleMoveToFolderWarning(true);
                toggleAllowFormSubmit(false);
                return true;
            }
            toggleAllowFormSubmit(false);
            toggleMoveToFolderWarning(false);
        } else {
            // Check retention period of the messages
            if (selectedMessages) {
                let isMessageExpired = false;
                let isFolderExpired = false;
                selectedMessages.some((f: any) => {
                    if (destinationFolder.expiresDays > f.expiresDays) {
                        isMessageExpired = true;
                        setWarningMessageText(FolderToolbarConstants.DESTINATION_FOLDER_GREATER_EXPIRY_WARNING_FOR_MESSAGE);
                        toggleMoveToFolderWarning(true);
                        toggleAllowFormSubmit(true);
                        return true;
                    }
                    if (destinationFolder.expiresDays < f.expiresDays) {
                        isFolderExpired = false;
                        setWarningMessageText(FolderToolbarConstants.DESTINATION_FOLDER_EXPIRY_WARNING_FOR_MESSAGE);
                        toggleMoveToFolderWarning(true);
                        toggleAllowFormSubmit(false);
                    }
                    if (destinationFolder.expiresDays == f.expiresDays) {
                        toggleAllowFormSubmit(false);
                        toggleMoveToFolderWarning(false);
                    }

                });

                return (isMessageExpired ? true : isFolderExpired ? false : true);
            }
        }
        return false;
    };

    const handleFolderSelection = (folder: IFolderModel) => {
        let continueSelection = true;

        if (!isMovingFolder) {
            // Scenarios while moving messages

            // Allow if folder is empty
            if (folder.subFolders?.length === 0) {
                continueSelection = true;
            }

            // Allow if folder has messages
            if (folder.folderType == ArchiveFolderType.MESSAGE) {
                continueSelection = true;
            }

            // Don't Allow for folder with multiple sub folders
            if (folder?.subFolders && folder?.subFolders?.length > 0) {
                toggleMoveToFolderWarning(true);
                setWarningMessageText(isDocumentRequest === true ? DocumentToolbarConstants.DR_MOVING_MULTIPLE_FOLDERS_WARNING : MessageToolbarConstants.MESSAGE_MOVING_MULTIPLE_FOLDERS_WARNING);
                continueSelection = false;
            }

            // Don't Allow for moving to same folder
            if (!isDocumentRequest) {
                if (selectedMessages && folder.folderId == selectedMessages[0].folderId) {
                    continueSelection = false;
                    return;
                }
            }
            else {
                if (selectedRequests && folder.folderId == selectedRequests[0].folderId) {
                    continueSelection = false;
                    return;
                }
            }
        }

        if (isMovingFolder) {
            // Scenarios while moving folder

            // Allow if folder is empty
            if (folder.subFolders?.length === 0) {
                continueSelection = true;
            }

            // Don't allow moving to folders containing messages
            if (folder.folderType == ArchiveFolderType.MESSAGE) {
                return;
            }

            // Don't Allow for moving to same folder
            if (selectedFolders && selectedFolders.length > 0 && selectedFolders[0].folderId == folder.folderId) {
                continueSelection = false;
                return;
            }

            // Don't Allow for moving to parent folder
            if (selectedFolders.length > 0 && selectedFolders[0].parentId === folder.folderId) {
                toggleMoveToFolderWarning(true);
                setWarningMessageText(FolderToolbarConstants.FOLDER_MOVING_TO_PARENT_FOLDER_WARNING);
                continueSelection = false;
            }

            // Don't allow for moving to sub folders of selected folder
            if (
                selectedFolders &&
                selectedFolders.length > 0 &&
                selectedFolders.some(node => node.path && folder.path?.includes(node.path))
            ) {
                continueSelection = false;
                return;
            }
        }

        if (!continueSelection) {
            toggleAllowFormSubmit(true);
        }
        if (continueSelection) {
            if (!isDocumentRequest) {
                checkFolderExpiry(folder!);
            } else {
                checkIfAllRequestsAreCompleted();
            }
        }
        const moveFolder = { ...folder };
        delete moveFolder.subFolders;
        setMoveToFolder(moveFolder);
    };

    const checkIfAllRequestsAreCompleted = () => {
        if (!(selectedRequests as IDocumentRequestListModel[]).every(request => request.status === DetailedStatus.COMPLETED)) {
            toggleMoveToFolderWarning(true);
            setWarningMessageText(FolderToolbarConstants.REQUESTS_INCLUDE_ACTIVE);
        }
        else {
            toggleMoveToFolderWarning(false);
            setWarningMessageText("");
        }
        toggleAllowFormSubmit(false);
    };

    const resetMoveToFolderPopup = () => {
        toggleMoveToFolderWarning(false);
        setMoveToFolder(undefined);
        dispatch(isMoveToFolderPopupOpen(false));
        Office.context.ui.messageParent("cancel");
    };

    const handleOnSubmitMoveToFolder = () => {
        if (isMovingFolder && moveToFolder) {
            dispatch(
                moveFolders(
                    selectedFolders.map(f => f.folderId),
                    selectedFolders,
                    moveToFolder,
                    (result: boolean) => {
                        if (result) {
                            const path = moveToFolder.path || "";
                            Office.context.ui.messageParent(path);
                        } else {
                            Office.context.ui.messageParent("close");
                        }

                        resetMoveToFolderPopup();
                        Office.context.ui.messageParent("close");
                    },
                    resourceLocators["MyFilesBtnMoveFolders"]
                )
            );
        } else if (moveToFolder) {
            Office.context.ui.messageParent(JSON.stringify(moveToFolder));
        }
    };

    const handleOnCreateFolder = (folder?: IFolderModel) => {
        getFoldersData();
        setShowAddFolderModal(false);
        dispatch(isMoveToFolderPopupOpen(true));
        if (folder) {
            handleFolderSelection(folder);
        }
    };

    return (
        <LoadingOverlay>
            <div className="move-to-container">
                <header>Move To</header>
                <main>
                    <Alert show={showMoveToFolderWarning} variant="warning">
                        {warningMessageText}
                    </Alert>

                    <TreeList
                        isMovingFolder={isMovingFolder}
                        folderType={+params.folderType}
                        isDR={isDocumentRequest}
                        handleFolderSelection={handleFolderSelection}
                        selectedMessages={selectedMessages}
                        selectedRequests={selectedRequests}
                        selectedFolders={selectedFolders}
                        destinationFolder={moveToFolder}
                        foldersTreeData={foldersTreeData.treeFolders}
                        showMoveToFolderWarning={showMoveToFolderWarning}
                        setShowAddFolderModal={setShowAddFolderModal}
                    />

                    <footer style={{ display: "flex", justifyContent: isDocumentRequest ? "space-between" : "flex-end", marginTop: "10px" }}>
                        {isDocumentRequest && (
                            <div style={{ display: "flex", alignItems: "center", fontSize: "12px" }}>
                                <InfoCircleIcon width={12} height={12} color={ColorConstants.brand_light_blue} />
                                <span className="info-padding">{DocumentToolbarConstants.DR_EXPIRY_DOES_NOT_APPLY}</span>
                            </div>
                        )}
                        <div className="footer-button-container" style={{ display: "flex", gap: "8px" }}>
                            <button
                                className="button-grey"
                                onClick={resetMoveToFolderPopup}
                            >
                                Cancel
                            </button>
                            <button type="button"
                                className="button-primary-blue-2"
                                disabled={allowFormSubmit}
                                onClick={handleOnSubmitMoveToFolder}
                            >
                                Ok
                            </button>
                        </div>
                    </footer>

                </main>
            </div>
            {showAddFolderModal &&
                <CreateFolderModal
                    show={showAddFolderModal}
                    onHide={() => setShowAddFolderModal(false)}
                    onSubmit={handleOnCreateFolder}
                    currentFolder={moveToFolder!}
                    modalType={FolderEventType.Create}
                    retentionOptions={retentionPeriods}
                    isSharedFolderDisabled={true}
                />
            }
            <Loader loading={false} />
        </LoadingOverlay>
    );
};
